import {
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core'
import {} from '@material-ui/core/colors'
import React, { Fragment, useEffect, useState } from 'react'
import kliosRepository from './kliosRepository'
import AddIcon from '@material-ui/icons/Add'
import { Snackbar } from 'elentari'
import { restApi } from '../../../../services/Api'
import ReplayIcon from '@material-ui/icons/Replay'

const KliosEmAnalise = ({
  handleRefresh,
  status,
  backgroundColor,
  intervalTime = 30000
}) => {
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const interval = setInterval(async () => {
      setLoading(true)
      await handleRefresh()

      setLoading(false)
    }, intervalTime)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item>
        <Chip
          label={status}
          style={{
            backgroundColor,
            color: '#fff'
          }}
        />
      </Grid>
      <Grid item>{loading && <CircularProgress size={12} />}</Grid>
    </Grid>
  )
}

const KliosConcluido = ({ urlPdf }) => {
  const [loading, setLoading] = useState(false)
  const theme = useTheme()

  const openPdf = async () => {
    setLoading(true)
    const response = await restApi.get(urlPdf, {}, { responseType: 'blob' })
    setLoading(false)

    if (response.ok) {
      const file = new Blob([response.data], { type: 'application/pdf' })

      const fileURL = URL.createObjectURL(file)
      window.open(fileURL)
    }
  }

  return (
    <Chip
      disabled={loading}
      onClick={openPdf}
      label={
        loading ? (
          <CircularProgress size={12} />
        ) : (
          'Concluído - Clique aqui para ver o PDF'
        )
      }
      style={{
        backgroundColor: theme.palette.success.main,
        color: '#fff'
      }}
    />
  )
}

const KliosError = ({ status, errorMessage, handleRecreate }) => {
  const theme = useTheme()

  const ChipFather = errorMessage ? Tooltip : Fragment

  return (
    <Grid container alignItems="center">
      <Grid item>
        <ChipFather
          title={
            <Typography variant="caption">
              Mensagem da Klios: {errorMessage}
            </Typography>
          }
        >
          <Chip
            label={status}
            style={{
              backgroundColor: theme.palette.error.main,
              color: '#fff'
            }}
          />
        </ChipFather>
      </Grid>

      <Grid item>
        <Reacreate handleRecreate={handleRecreate} />
      </Grid>
    </Grid>
  )
}

const Reacreate = ({ handleRecreate }) => {
  const [creating, setCreating] = useState(false)

  const onReacreate = async () => {
    setCreating(true)
    await handleRecreate()
    setCreating(false)
  }

  return (
    <Tooltip
      title={
        <Typography variant="caption">
          Reenviar solicitação de análise para a Klios.
          <br />
          Atenção: Essa ação faz uma nova solicitação à Klios e substitui o
          resultado da integração atual.
        </Typography>
      }
    >
      <IconButton disabled={creating} onClick={onReacreate}>
        {creating ? <CircularProgress size={12} /> : <ReplayIcon />}
      </IconButton>
    </Tooltip>
  )
}

const KliosCadastrado = ({ handleRecreate }) => {
  const theme = useTheme()

  return (
    <Grid container alignItems="center">
      <Grid item>
        <Chip
          label="Cadastrado"
          style={{
            backgroundColor: theme.palette.info.main,
            color: '#fff'
          }}
        />
      </Grid>

      <Grid item>
        <Reacreate handleRecreate={handleRecreate} />
      </Grid>
    </Grid>
  )
}

const KliosMapper = ({
  klios,
  handleRefresh,
  handleRecreate,
  handleRefreshFromDb
}) => {
  const theme = useTheme()

  if (klios?.status === 'Cadastrado') {
    return <KliosCadastrado handleRecreate={handleRecreate} />
  }

  if (klios?.status === 'Concluído') {
    return <KliosConcluido {...klios} />
  }

  if (klios?.status === 'Em Análise') {
    return (
      <KliosEmAnalise
        status={klios.status}
        handleRefresh={handleRefresh}
        backgroundColor={theme.palette.warning.main}
      />
    )
  }

  if (klios?.status === 'Enviando') {
    return (
      <KliosEmAnalise
        status={klios.status}
        handleRefresh={handleRefreshFromDb}
        backgroundColor={theme.palette.info.main}
        intervalTime={5000}
      />
    )
  }

  if (klios?.status?.includes('Erro')) {
    const splittedError = klios.status.split(': ')

    return (
      <KliosError
        status={splittedError[0]}
        errorMessage={splittedError[1]}
        handleRecreate={handleRecreate}
      />
    )
  }

  return (
    <KliosError
      status={klios?.status || klios?.error?.message || 'Erro desconhecido'}
      handleRecreate={handleRecreate}
    />
  )
}

const IntegracaoKlios = ({ solicitacaoId }) => {
  const [initialLoading, setInitialLoading] = useState(true)
  const [klios, setKlios] = useState(null)
  const [message, setMessage] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    const getData = async () => {
      setInitialLoading(true)
      const result = await kliosRepository.fetch(solicitacaoId)

      if (!result.ok) {
        setMessage(result.data.message)
      }

      setKlios(result.data)
      setInitialLoading(false)
    }

    getData()
  }, [])

  const handleCreate = async () => {
    setCreating(true)
    const result = await kliosRepository.create(solicitacaoId)
    setCreating(false)

    if (!result.ok) {
      const resultGet = await kliosRepository.fetch(solicitacaoId)

      return setKlios(resultGet.data)
    }

    setKlios(result.data)
  }

  const handleRecreate = async () => {
    const result = await kliosRepository.recreate(solicitacaoId)

    if (!result.ok) {
      const resultGet = await kliosRepository.fetch(solicitacaoId)

      return setKlios(resultGet.data)
    }

    setKlios(result.data)
  }

  const handleRefresh = async () => {
    const result = await kliosRepository.refreshResult(solicitacaoId)

    if (!result.ok) {
      setMessage(result.data.message)
    }

    setKlios(result.data)
  }

  const handleRefreshFromDb = async () => {
    const result = await kliosRepository.fetch(solicitacaoId)

    if (!result.ok) {
      setMessage(result.data.message)
    }

    setKlios(result.data)
  }

  return (
    <>
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <Typography variant="h6">Klios </Typography>
        </Grid>
        <Grid item>
          {klios && (
            <KliosMapper
              klios={klios}
              handleRefresh={handleRefresh}
              handleRefreshFromDb={handleRefreshFromDb}
              handleRecreate={handleRecreate}
            />
          )}

          {!klios ? (
            initialLoading ? (
              <CircularProgress size={24} />
            ) : (
              <IconButton
                disabled={creating}
                size="small"
                onClick={handleCreate}
              >
                <Tooltip
                  title={
                    <Typography variant="caption">
                      {creating
                        ? 'Fazendo integração com a Klios, aguarde...'
                        : 'Iniciar integração com a Klios'}
                    </Typography>
                  }
                >
                  {creating ? <CircularProgress size={12} /> : <AddIcon />}
                </Tooltip>
              </IconButton>
            )
          ) : null}
        </Grid>
      </Grid>
      <Snackbar
        onClose={() => {
          setMessage('')
        }}
        autoHideDuration={2000}
        message={message}
      />
    </>
  )
}

export default IntegracaoKlios
